import { useEffect, useRef, useState } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import CategoryBadgesContainer from '@components/user/order/CategoryBadgesContainer';
import useWorkspace from '@hooks/user/useWorkspace';
import { Product, ProductStatus } from '@@types/index';
import _ from 'lodash';
import OrderButton from '@components/user/order/OrderButton';
import useProduct from '@hooks/user/useProduct';
import { colFlex } from '@styles/flexStyles';
import { Color } from '@resources/colors';
import OrderImageSlider from '@components/user/order/OrderImageSlider';
import OrderStickyNavBar from '@components/user/order/OrderStickyNavBar';
import WorkspaceNotice from '@components/user/order/WorkspaceNotice';
import OrderProductContent from './OrderProductContent';
import { useAtomValue } from 'jotai';
import { userCategoriesAtom, userOrderBasketAtom, userProductsAtom, userWorkspaceAtom } from '@jotai/user/atoms';
import { calculateBasketTotalAmount, productsToGaItems } from '@utils/orderBasket';
import { saveOrderPageHistoryIndex } from '@utils/orderPageHistory';
import { trackEvent } from '@utils/analytics';
import { GA_EVENT } from '@constants/analytics';

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
  ${colFlex({ align: 'center' })}
`;

const StickyHeader = styled.div`
  width: 100%;
  position: sticky;
  top: 45px;
  background: ${Color.WHITE};
  z-index: 1000;
`;

const Header = styled.div`
  width: 100%;
  min-height: 120px;
  word-break: keep-all;
  ${colFlex({ justify: 'center', align: 'center' })}
  gap: 7px;
`;

const Title = styled.div`
  font-size: 25px;
  font-weight: 600;
  text-align: center;
`;

const Description = styled.div`
  font-size: 18px;
  color: ${Color.GREY};
  text-align: center;
  white-space: pre-wrap;
`;

function Order() {
  const workspace = useAtomValue(userWorkspaceAtom);
  const isShowNotice = workspace.notice.length > 0;
  const rawProductCategories = useAtomValue(userCategoriesAtom);
  const rawProducts = useAtomValue(userProductsAtom);
  const sellableProducts = rawProducts.filter((it) => it.status === ProductStatus.SELLING);

  const productsByCategoryId = _.groupBy<Product>(sellableProducts, (product) => product.productCategory?.id);

  const productsMap = _.keyBy(rawProducts, 'id');

  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get('workspaceId');
  const isPreview = searchParams.get('preview') === 'true';

  const { fetchWorkspace } = useWorkspace();
  const { fetchCategories, fetchProducts } = useProduct(workspaceId);
  const navigate = useNavigate();
  const orderBasket = useAtomValue(userOrderBasketAtom);
  const totalAmount = calculateBasketTotalAmount(orderBasket, productsMap);

  const headerRef = useRef<HTMLDivElement>(null);
  const [showNavBar, setShowNavBar] = useState(false);

  useEffect(() => {
    saveOrderPageHistoryIndex();
    fetchWorkspace(workspaceId);
    fetchCategories();
    fetchProducts();

    const updateStickyNavBarVisibility = () => {
      if (!headerRef.current) return;

      const bufferedHeight = 65;
      const headerBottom = headerRef.current.getBoundingClientRect().bottom;
      const isShow = headerBottom - bufferedHeight <= 0;

      setShowNavBar(isShow);
    };

    window.addEventListener('scroll', updateStickyNavBarVisibility);

    return () => window.removeEventListener('scroll', updateStickyNavBarVisibility);
  }, []);

  const hasTrackedViewItemListRef = useRef(false);

  useEffect(() => {
    if (isPreview || hasTrackedViewItemListRef.current || sellableProducts.length === 0) return;

    hasTrackedViewItemListRef.current = true;
    trackEvent(GA_EVENT.VIEW_ITEM_LIST, { items: productsToGaItems(sellableProducts) });
  }, [isPreview, sellableProducts]);

  return (
    <Container className={'order-container'}>
      <OrderImageSlider images={workspace.images} />

      <Header ref={headerRef}>
        <Title>{workspace.name}</Title>
        <Description>{workspace.description}</Description>
      </Header>

      <OrderStickyNavBar showNavBar={showNavBar} workspaceName={workspace.name} />

      <StickyHeader>
        <CategoryBadgesContainer productCategories={rawProductCategories} productsByCategory={productsByCategoryId} />
      </StickyHeader>

      {isShowNotice && <WorkspaceNotice notice={workspace.notice} />}

      <OrderProductContent />

      <OrderButton
        showButton={orderBasket.length > 0}
        buttonLabel={`${totalAmount.toLocaleString()}원 장바구니`}
        onClick={() => {
          if (isPreview) {
            alert('미리보기 모드에서는 주문이 불가능합니다.');
            return;
          }
          navigate({
            pathname: '/order-basket',
            search: createSearchParams(searchParams).toString(),
          });
        }}
      />
    </Container>
  );
}

export default Order;
